import React, { useState, useEffect, useCallback } from 'react';

function MeliMetricsModule() {
  const [metrics, setMetrics] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'visits', direction: 'desc' });

  const API_BASE = (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/+$/, '')) || (window.location.hostname === 'localhost' ? 'http://localhost:3000' : window.location.origin);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/meli/metrics`);
      const data = await res.json();
      if (data.success) {
        setMetrics(data.data);
        setSummary(data.summary);
      } else {
        setError('No se pudieron cargar las métricas');
      }
    } catch (err) {
      console.error(err);
      setError('Error de red al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedMetrics = [...metrics].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-500 ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
        <p className="text-gray-600 font-medium animate-pulse">Sincronizando con Mercado Libre...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto font-sans">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Analíticas Meli <span className="text-xl">📈</span></h1>
          <p className="text-gray-500 mt-1">Rendimiento de tus publicaciones activas (Últimos 30 días)</p>
        </div>
        <button 
          onClick={fetchMetrics}
          className="mt-4 md:mt-0 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 border border-gray-300 rounded-lg shadow-sm flex items-center transition-all"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
          Actualizar Datos
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <p className="text-red-700 font-medium">{error}</p>
          <button onClick={fetchMetrics} className="mt-2 text-sm text-red-600 underline">Reintentar</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-blue-100 font-medium mb-1">Total Publicaciones</p>
            <h2 className="text-4xl font-bold">{summary.total_items || 0}</h2>
          </div>
          <div className="absolute right-[-10px] bottom-[-20px] opacity-20">
            <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"></path></svg>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-orange-50 font-medium mb-1">Visitas (30 días)</p>
            <h2 className="text-4xl font-bold">{summary.total_visits?.toLocaleString() || 0}</h2>
          </div>
          <div className="absolute right-[-10px] bottom-[-20px] opacity-20">
            <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path></svg>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-emerald-50 font-medium mb-1">Ventas Totales (Histórico)</p>
            <h2 className="text-4xl font-bold">{summary.total_sales?.toLocaleString() || 0}</h2>
          </div>
          <div className="absolute right-[-10px] bottom-[-20px] opacity-20">
            <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20"><path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"></path></svg>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('title')}>
                  Publicación <SortIcon columnKey="title" />
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors text-right" onClick={() => handleSort('price')}>
                  Precio <SortIcon columnKey="price" />
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors text-center" onClick={() => handleSort('available_quantity')}>
                  Stock <SortIcon columnKey="available_quantity" />
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors text-center" onClick={() => handleSort('visits')}>
                  Visitas (30d) <SortIcon columnKey="visits" />
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors text-center" onClick={() => handleSort('sold_quantity')}>
                  Ventas <SortIcon columnKey="sold_quantity" />
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors text-center" onClick={() => handleSort('conversion_rate')}>
                  Conversión <SortIcon columnKey="conversion_rate" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedMetrics.map((item) => (
                <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <img className="h-10 w-10 object-contain" src={item.thumbnail} alt="" />
                      </div>
                      <div className="ml-4 max-w-xs md:max-w-md lg:max-w-lg truncate">
                        <a href={item.permalink} target="_blank" rel="noreferrer" className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block">
                          {item.title}
                        </a>
                        <div className="text-xs text-gray-500 mt-0.5">{item.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm font-medium text-gray-900">${item.price?.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.available_quantity > 10 ? 'bg-green-100 text-green-800' : item.available_quantity > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.available_quantity} uds
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-sm font-bold text-gray-900">{item.visits?.toLocaleString()}</div>
                    {/* Visual bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min((item.visits / Math.max(...metrics.map(m => m.visits))) * 100, 100)}%` }}></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-sm font-bold text-gray-900">{item.sold_quantity?.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className={`text-sm font-bold ${item.conversion_rate > 5 ? 'text-green-600' : item.conversion_rate > 1 ? 'text-yellow-600' : 'text-gray-500'}`}>
                      {item.conversion_rate}%
                    </div>
                  </td>
                </tr>
              ))}
              
              {sortedMetrics.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No se encontraron publicaciones activas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MeliMetricsModule;
